// Client-side image processor used before R2 upload. Two modes:
//   - Product (default): trim near-white whitespace, pad to square, encode WebP
//   - Banner (preserveAspectRatio): keep aspect, cap width at 2560, encode WebP
//
// Ported 1:1 from miluliem so vinoga admin behaviour matches across projects.

interface ProcessImageOptions {
  /** Skip the square-crop / whitespace-trim pass when natural aspect matters
   *  (banners, collection covers, blog cover images). Default false. */
  preserveAspectRatio?: boolean;
}

export async function processImage(
  file: File,
  options: ProcessImageOptions = {},
): Promise<Blob> {
  const { preserveAspectRatio = false } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Canvas not supported");

      if (preserveAspectRatio) {
        // Banner mode: WebP encode + cap at 2560 px wide so we don't ship 5MB hero images.
        const MAX_WIDTH = 2560;
        const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject("Failed to output blob")),
          "image/webp",
          0.92,
        );
        return;
      }

      // Product mode: trim near-white, pad to square (+2% padding), WebP encode.
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const THRESHOLD = 245;
      const isEmpty = (r: number, g: number, b: number, a: number) =>
        a === 0 || (r >= THRESHOLD && g >= THRESHOLD && b >= THRESHOLD);

      let top = -1, bottom = -1, left = -1, right = -1;
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const idx = (y * canvas.width + x) * 4;
          if (!isEmpty(data[idx]!, data[idx + 1]!, data[idx + 2]!, data[idx + 3]!)) {
            if (top === -1) top = y;
            bottom = Math.max(bottom, y);
            if (left === -1) left = x;
            else left = Math.min(left, x);
            right = Math.max(right, x);
          }
        }
      }
      if (top === -1) {
        top = 0; bottom = img.height; left = 0; right = img.width;
      }
      const trimmedW = right - left + 1;
      const trimmedH = bottom - top + 1;
      const maxDim = Math.max(trimmedW, trimmedH);
      // 2% (was 10%): the product fills ~96% of the square, matching the
      // legacy catalogue photos (median ~95%). At 10% fresh uploads rendered
      // ~15% smaller than older products on storefront cards.
      const padding = Math.floor(maxDim * 0.02);
      const finalSize = maxDim + padding * 2;

      canvas.width = finalSize;
      canvas.height = finalSize;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalSize, finalSize);
      const drawX = Math.floor((finalSize - trimmedW) / 2);
      const drawY = Math.floor((finalSize - trimmedH) / 2);
      ctx.drawImage(img, left, top, trimmedW, trimmedH, drawX, drawY, trimmedW, trimmedH);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject("Failed to output blob")),
        "image/webp",
        0.90,
      );
    };
    img.onerror = () => reject("Failed to load image");

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) img.src = e.target.result as string;
    };
    reader.readAsDataURL(file);
  });
}
